import { useRef } from 'react';

/**
 * PLAT-11150 — hold a value steady while its surface is off screen.
 *
 * The Clip Preview keeps every tab mounted behind `display: none`, so a hidden
 * pane still re-renders on every store change. Where the expensive child is
 * memoized on reference identity — `TranscriptionText` compares its
 * `transcriptElements` and `timelineMarkers` props with `===` — handing it a
 * freshly-derived value each time defeats that memo and re-renders thousands of
 * nodes nobody is looking at.
 *
 * Returning the last value seen while active keeps that identity stable, and
 * the pane simply shows what it last showed until its tab is opened again.
 *
 * The ref is written during render rather than in an effect (CLAUDE.md pitfall
 * 17): an effect would publish the new value one commit late, so the first
 * render after re-activation would still hand down the stale one.
 *
 * Note this only holds values that flow as *props*. An `observer` reading a
 * MobX store directly re-renders regardless — freezing that path means not
 * reading the observable at all, which is what `createFrozenElementsReader`
 * does for the transcript itself.
 */
export function useFrozenWhileInactive<T>(value: T, isActive: boolean): T {
  const lastActiveRef = useRef(value);
  if (isActive) lastActiveRef.current = value;
  return lastActiveRef.current;
}
